import clsx from 'clsx';
import React, { ReactNode } from 'react';

import AnchorLink from '@/src/components/common/Elements/AnchorLink/AnchorLink';
import Icon, { ICONS } from '@/src/components/common/Elements/Icon/Icon';
import { ACTION_POSITIONS, MODAL_TYPE } from '@/src/components/common/Elements/Modal/Config';
import Modal from '@/src/components/common/Elements/Modal/Modal';
import ModalButton from '@/src/components/common/Elements/Modal/ModalButton';
import { cn } from '@/src/util/ui';

type ModalPanelProps = {
  id?: string;
  title?;
  className?: string;
  message?: string;
  containerClasses?: string;
  modalType?;
  actionPosition?;
  open?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  cancelBtn?;
  confirmBtn?;
  children?: ReactNode;
};

const ModalPanel = ({
  id = '',
  title,
  className,
  containerClasses = '',
  modalType = MODAL_TYPE.default,
  actionPosition = ACTION_POSITIONS.between,
  message = 'Do you wish to proceed?',
  cancelBtn = null,
  confirmBtn = null,
  open = true,
  onClose,
  onConfirm,
  children,
}: ModalPanelProps) => {
  const { headerTextClasses, btnConfirmClasses, modalClasses } = modalType;

  //  modal body content
  const body = children ? (
    children
  ) : (
    <div className={clsx('tw-text-cfo-black', 'tw-m-0')}>{message}</div>
  );

  //   modal footer actions
  const cancelButton = (
    <ModalButton
      className={clsx(
        'btn--cancel',
        'tw-text-cfo-black',
        'tw-mr-[5px]',
        'tw-border-cfo-grey-500 tw-bg-cfo-grey-100'
      )}
      btn={cancelBtn}
      label={(cancelBtn && cancelBtn.label) || 'Cancel'}
      onClick={onClose}
    />
  );
  const confirmButton = (
    <ModalButton
      className={cn(
        'btn--confirm',
        confirmBtn && confirmBtn.disabled
          ? 'tw-text-cfo-grey-600 tw-border-cfo-grey-500 tw-bg-cfo-grey-100'
          : btnConfirmClasses
      )}
      btn={confirmBtn}
      label={(confirmBtn && confirmBtn.label) || 'Confirm'}
      onClick={onConfirm}
      disabled={confirmBtn && confirmBtn.disabled}
    />
  );

  return (
    <Modal position="right" id={id} className={cn(className)}>
      {/* Note: !important is used on some tailwind classes here to override the default styles in vendor.css */}

      <div className={cn('modal-dialog', containerClasses)} role="document">
        <div className={clsx('modal-content', 'tw-relative')}>
          {/* Modal Header */}
          <div className={cn('modal-header', 'tw-border-b-0', 'tw-p-[20px]', modalClasses)}>
            <div className={clsx('tw-flex tw-items-center tw-justify-between')}>
              <p
                className={cn(
                  'tw-text-[18px] tw-font-brandonTextMedium',
                  'tw-m-0 ',
                  headerTextClasses ? headerTextClasses : 'tw-text-cfo-black '
                )}
              >
                {title}
              </p>
              <AnchorLink
                className={cn(headerTextClasses ? headerTextClasses : 'tw-text-cfo-black ')}
                data-dismiss="modal"
                aria-label="Close"
                onClick={onClose}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    onClose();
                  }
                }}
              >
                <Icon
                  className={cn(
                    'tw-text-[22px]',
                    headerTextClasses ? headerTextClasses : 'tw-text-cfo-black '
                  )}
                  icon={ICONS.close}
                />
              </AnchorLink>
            </div>
          </div>

          {/* Modal Body */}
          <div
            className={clsx(
              'modal-body',
              '!tw-py-[20px] !tw-px-0',
              'tw-border-solid tw-border-t-[1px] tw-border-t-cfo-grey-500'
            )}
          >
            {body}
          </div>

          {/* Modal Footer */}
          <div
            className={clsx(
              'modal-footer',
              'tw-fixed tw-left-0 tw-right-0 tw-bottom-0',
              'tw-py-[15px] tw-px-[20px]',
              '!tw-border-t-cfo-grey-500'
            )}
          >
            <div className={cn('tw-flex tw-items-center', actionPosition)}>
              {/* Footer Action: Cancel button */}
              {cancelButton}

              {/* Footer Action: Confirm button */}
              {confirmButton}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ModalPanel;
